// src/components/pro/proFeatures.ts — 2-oct-2026
// ============================================================================
// CATÁLOGO ÚNICO de lo que ve un negocio GRATIS al entrar a una función PRO.
// Antes cada pantalla bloqueaba a su manera (una línea, cuatro beneficios, o nada).
// Plan: forobeta-ops/docs/lealify-pantallas-pro-plan-20261002.md
//
// Función PRO nueva = una entrada aquí + <ProGate feature="…"> en su página. Nace
// bloqueada y explicada. Textos en el idioma del DUEÑO: qué problema le resuelve,
// no cómo se llama la función. «Así se ve» = datos de EJEMPLO, nunca del negocio.
// ============================================================================

export type ProExample =
  | { kind: 'bubble'; message: string; /** a quién le llega (rótulo sobre la burbuja) */ to: string }
  | { kind: 'rows'; rows: { primary: string; secondary: string; badge?: string; tone?: 'ok' | 'warn' | 'bad' }[] }
  | { kind: 'chips'; chips: string[]; caption: string }
  | { kind: 'giftcard'; amount: string; occasion: string };

export interface ProFeature {
  /** Nombre corto de la función (en el botón no se usa; sí en métricas y textos). */
  name: string;
  /** Una frase: el problema que resuelve. */
  pitch: string;
  /** Exactamente 3 beneficios concretos. */
  benefits: [string, string, string];
  /** La función manda mensajes por WhatsApp → se aclara que incluye conectar el del negocio. */
  whatsapp?: boolean;
  example: ProExample;
}

export const PRO_FEATURES = {
  'inactive-clients': {
    name: 'Clientes Inactivos',
    pitch: 'Descubre quién dejó de venir antes de perderlo para siempre.',
    benefits: [
      'Lista de clientes que no han vuelto en 30, 60 o 90 días',
      'Su última visita y sus puntos, a la vista',
      'Mándales un mensaje para que regresen',
    ],
    example: { kind: 'rows', rows: [
      { primary: 'Ana López', secondary: 'Última visita hace 47 días · 120 pts', badge: 'Inactiva', tone: 'warn' },
      { primary: 'Luis Pérez', secondary: 'Última visita hace 63 días · 85 pts', badge: 'Inactivo', tone: 'warn' },
      { primary: 'María Ruiz', secondary: 'Última visita hace 91 días · 40 pts', badge: 'Inactiva', tone: 'bad' },
    ] },
  },
  'unsatisfied-clients': {
    name: 'Clientes Insatisfechos',
    pitch: 'Entérate de quién salió descontento y recupéralo a tiempo.',
    benefits: [
      'Todas las calificaciones «malo» y «neutral» en un solo lugar',
      'Lleva el seguimiento: por contactar, contactado, resuelto',
      'Mensajes de disculpa listos para mandar',
    ],
    example: { kind: 'rows', rows: [
      { primary: 'Ana López', secondary: 'Calificó «neutral» · hace 2 días', badge: 'Por contactar', tone: 'warn' },
      { primary: 'Luis Pérez', secondary: 'Calificó «malo» · hace 5 días', badge: 'Resuelto', tone: 'ok' },
    ] },
  },
  'birthday-panel': {
    name: 'Cumpleaños',
    pitch: 'Felicita a cada cliente en su día, sin tener que acordarte.',
    benefits: [
      'Felicitación automática por WhatsApp el día de su cumpleaños',
      'Lista de cumpleañeros del mes',
      'Un resumen cada mañana de a quién se felicitó',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Feliz cumpleaños, Ana! 🎂\n\nEn tu día queremos consentirte: pasa esta semana y tu bebida va por nuestra cuenta. ¡Te esperamos!' },
  },
  referrals: {
    name: 'Referidos',
    pitch: 'Que tus clientes te traigan clientes nuevos.',
    benefits: [
      'Puntos automáticos para quien recomienda y para el nuevo',
      'Ranking de quién te trae más clientes',
      'Historial completo de cada referido',
    ],
    example: { kind: 'rows', rows: [
      { primary: '1. Ana López', secondary: '6 amigos registrados · 300 pts ganados', badge: 'Top', tone: 'ok' },
      { primary: '2. Luis Pérez', secondary: '3 amigos registrados · 150 pts ganados' },
      { primary: '3. María Ruiz', secondary: '1 amiga registrada · 50 pts ganados' },
    ] },
  },
  'recharge-points': {
    name: 'Gestionar Puntos',
    pitch: 'Corrige o regala puntos a mano cuando lo necesites.',
    benefits: [
      'Suma o resta puntos a cualquier cliente',
      'Cada ajuste queda registrado con quién lo hizo',
      'Ideal para compensar un error o premiar a alguien',
    ],
    example: { kind: 'rows', rows: [
      { primary: 'Ana López · 120 pts', secondary: '«Olvidó escanear ayer» · por Sofía', badge: '+20', tone: 'ok' },
      { primary: 'Luis Pérez · 65 pts', secondary: '«Visita duplicada» · por Diego', badge: '−15', tone: 'bad' },
    ] },
  },
  'gift-cards': {
    name: 'Gift Cards',
    pitch: 'Regala puntos con una tarjeta bonita que llega por WhatsApp.',
    benefits: [
      'Tarjeta con tu logo y diseños de temporada',
      'Le llega a la persona por WhatsApp',
      'Se canjea sola cuando se registra en el kiosko',
    ],
    whatsapp: true,
    example: { kind: 'giftcard', amount: '100 puntos', occasion: 'Para ti, Ana 🎁' },
  },
  campaigns: {
    name: 'Campañas',
    pitch: 'Mándale una promoción a los clientes correctos, sin arriesgar tu número.',
    benefits: [
      'Elige a quién: inactivos, cumpleañeros, por etiqueta',
      'Envío gradual que cuida tu WhatsApp',
      'Por WhatsApp o por correo, con sus resultados',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Hola Ana! ☕ Te extrañamos.\n\nEsta semana tu segundo latte va a mitad de precio. Solo muestra este mensaje en caja.' },
  },
  tags: {
    name: 'Etiquetas',
    pitch: 'Organiza a tus clientes para hablarle a cada grupo a su manera.',
    benefits: [
      'Etiquetas como VIP, frecuente o mayoreo',
      'Etiqueta a muchos de una vez (por recompensa o por campaña)',
      'Úsalas para elegir a quién va cada campaña',
    ],
    example: { kind: 'chips', chips: ['VIP · 24', 'Frecuente · 118', 'Café de grano · 37', 'Empresa · 9'], caption: 'Tus etiquetas y cuántos clientes tiene cada una' },
  },
  'welcome-message': {
    name: 'Mensaje de Bienvenida',
    pitch: 'Que cada cliente nuevo reciba un saludo de tu negocio al registrarse.',
    benefits: [
      'Sale solo, por WhatsApp o por correo',
      'Con tu texto y hasta 3 imágenes',
      'Le dice con cuántos puntos empieza',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Hola Ana! 🎉 Bienvenida a tu cafetería favorita.\n\nYa tienes 10 puntos para empezar. ¡Gracias por registrarte!' },
  },
  'visit-message': {
    name: 'Mensaje de Visita',
    pitch: 'Dale las gracias a cada cliente que regresa.',
    benefits: [
      'Sale solo al registrar su visita',
      'Le dice cuántos puntos lleva y cuánto le falta para su premio',
      'Con tu texto y tus emojis',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Gracias por volver, Ana! ☕\n\nLlevas 80 puntos: te faltan 20 para tu bebida gratis.' },
  },
  'referral-message': {
    name: 'Mensaje de Referido',
    pitch: 'Avísale a quien te recomendó que ya ganó sus puntos.',
    benefits: [
      'Sale solo cuando su invitado se registra',
      'Le confirma los puntos que ganó',
      'Lo motiva a seguir recomendándote',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Ana! 🙌 Tu amiga Laura ya se registró gracias a ti.\n\nTe sumamos 50 puntos. ¡Gracias por recomendarnos!' },
  },
  'feedback-alert': {
    name: 'Alerta de Opinión',
    pitch: 'Entérate al momento cuando alguien califica mal su visita.',
    benefits: [
      'Te llega un WhatsApp con el nombre y la calificación',
      'Para llamarle ese mismo día',
      'Antes de que lo publique en Google',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así te llega a ti', message: '⚠️ Calificación baja\n\n👤 Ana López calificó su visita en el kiosko como «malo».\n\nConviene escribirle o llamarle hoy mismo.' },
  },
  'low-balance-alert': {
    name: 'Aviso de Saldo Bajo',
    pitch: 'Invita a volver a quien se quedó sin puntos después de canjear.',
    benefits: [
      'Sale solo después de un canje',
      'Le recuerda lo que le falta para su siguiente premio',
      'Una sola vez, sin insistir',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Disfruta tu premio, Ana! 🎁\n\nTe quedaron 10 puntos: con 4 visitas más tienes otro. ¡Te esperamos!' },
  },
  'membership-notice': {
    name: 'Avisos de membresía',
    pitch: 'Avisa solo a cada miembro cuando su membresía está por vencer, para que renueve a tiempo.',
    benefits: [
      'Por correo (o WhatsApp), unos días antes o cuando queden pocos productos',
      'Una sola vez por membresía; al renovar vuelve a contar',
      'Se enciende por plan, desde Membresías',
    ],
    whatsapp: false,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: 'Hola Ana 👋 Tu membresía Club del Café vence el 19 nov (te quedan 7 días). Pasa a renovarla y sigue disfrutando tus beneficios 🎟️' },
  },
  'google-reviews': {
    name: 'Reseñas en Google',
    pitch: 'Más reseñas de 5 estrellas en Google, de tus clientes más contentos.',
    benefits: [
      'Solo a clientes frecuentes que calificaron «Excelente»',
      'Una sola vez por cliente, en el mejor momento',
      'Ves cada día cuántas reseñas nuevas llevas',
    ],
    whatsapp: true,
    example: { kind: 'bubble', to: 'Así le llega a tu cliente', message: '¡Hola Ana! ☕ Ya eres de casa, gracias por volver.\n\n¿Nos ayudas con algo? Una reseña tuya en Google nos ayuda muchísimo a que más gente nos encuentre:\n👉 (enlace directo a escribir tu reseña)\n\nEs la única vez que te lo pedimos. ¡Gracias!' },
  },
  whatsapp: {
    name: 'WhatsApp del negocio',
    pitch: 'Que todos los mensajes salgan desde el WhatsApp de tu negocio.',
    benefits: [
      'Conectas tu número con un código QR',
      'Bienvenidas, visitas y promociones salen desde ahí',
      'Tus clientes te contestan directo a ti',
    ],
    example: { kind: 'bubble', to: 'Desde el número de tu negocio', message: '¡Hola Ana! 👋 Gracias por tu visita de hoy. Llevas 80 puntos ⭐' },
  },
  'team-invite': {
    name: 'Equipo',
    pitch: 'Dale acceso a tu equipo, cada quien con su propio usuario.',
    benefits: [
      'Invita a gerentes y empleados por correo',
      'Cada rol ve solo lo que le toca',
      'Sabes quién registró cada visita o ajuste',
    ],
    example: { kind: 'rows', rows: [
      { primary: 'Sofía', secondary: 'Registra clientes y maneja saldos', badge: 'Gerente', tone: 'ok' },
      { primary: 'Diego', secondary: 'Registra visitas y canjes', badge: 'Empleado' },
    ] },
  },
  // Reporte «Por empleado» (23-oct-2026, Hogar China City): va con el equipo (PRO). Gratis ve solo lo suyo.
  'staff-report': {
    name: 'Reporte por empleado',
    pitch: 'Con PRO invitas a tu equipo y ves cuántos clientes registró y cuánto canjeó cada quien, día por día.',
    benefits: [
      'Clientes nuevos, compras y canjes de cada empleado',
      'Desglose por día y por sucursal, con descarga a Excel',
      'Ajustes y cancelaciones a nombre de quien los hizo',
    ],
    example: { kind: 'rows', rows: [
      { primary: 'Sofía', secondary: '12 clientes nuevos · 48 compras · 6 canjes', badge: 'Hoy', tone: 'ok' },
      { primary: 'Diego', secondary: '4 clientes nuevos · 31 compras · 2 canjes' },
    ] },
  },
} satisfies Record<string, ProFeature>;

export type ProFeatureKey = keyof typeof PRO_FEATURES;
// Config. Gift Cards comparte la explicación de Gift Cards.
export const proFeature = (key: ProFeatureKey): ProFeature => PRO_FEATURES[key];
